import { autoinject, transient, BindingEngine, observable, computedFrom, Disposable } from "aurelia-framework";
import { UnitOfWorkObserver } from "../../../../../../utilities/unit-of-work-observer";
import { type CustomReportModel } from "../../../../../../models/custom-reports/custom-reports.models";
import { ClientSideValidator, EntityValidationsService } from "../../../../../../client-validation-system/validations-service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { CustomReportService } from "../../../../../../services/custom-report/custom-report.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Guid } from "../../../../../../utilities/guid";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../client-validation-system/validations.models";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { QueryFilter, DataGridColumn, EntityMetaDataModel, QueryColumnHelper } from "../../../../../../models/queries/queries.models";
import { QueryBuilder } from "../../../../../components/queries/query-builder";
import * as Enumerable from "linq";
import { QueriesClientService } from "../../../../../../services/queries/queries-client.services";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";

@autoinject()
@transient()
export class EditCustomReportDialog {

    private _subscriptions: Disposable[] = [];
    private _uow: UnitOfWorkObserver<CustomReportModel>;
    private _whereQueryBuilder: QueryBuilder;

    public validator: ClientSideValidator;
    public onComplete: () => void;
    public whereClauses: QueryFilter[] = [];
    public columns: DataGridColumn[] = [];
    public availableColumns: DataGridColumn[] = [];
    public visibleColumns: DataGridColumn[] = [];
    public selectedAvailableColumn: DataGridColumn;
    public selectedVisibleColumn: DataGridColumn;
    public entityModels: EntityMetaDataModel[];
    public isExcelUploaded: boolean;
    public addFilterCalled: boolean;
    public selectedCustomReportType: LookupValue;
    public isLoading: boolean = true;

    @observable()
    public filesInternal: FileList;

    @observable()
    public item: CustomReportModel;

    @observable({ changeHandler: 'resetColumns' })
    availableColumnsQuery: string;

    @observable({ changeHandler: 'resetColumns' })
    visibleColumnsQuery: string;

    @computedFrom('_whereQueryBuilder')
    get whereQueryBuilder(): QueryBuilder {
        return this._whereQueryBuilder;
    }

    set whereQueryBuilder(value: QueryBuilder) {
        this._whereQueryBuilder = value;
        if (value && this.whereClauses.length == 0 && this.item && !this.addFilterCalled) {
            setTimeout(() => {
                this._whereQueryBuilder.addFilter(0);
                this.addFilterCalled = true;
            })
        }
    }

    public constructor(private dialogClientService: DialogClientService,
        private entityValidationsService: EntityValidationsService,
        private queriesClientService: QueriesClientService,
        private bindingEngine: BindingEngine,
        private notificationClientService: NotificationClientService,
        private customReportService: CustomReportService,
        private validationsClientService: ValidationsClientService) {

    }

    activate(model: { onComplete: () => void, id: number }) {
        this.isLoading = true;
        this.onComplete = model.onComplete;

        this.queriesClientService.getEntityModels().then(response => {
            this.entityModels = response;

            if (model.id) {
                this.customReportService.getCustomReport(model.id).then(x => {
    
                    const customReport = x.customReport;
                    this.isExcelUploaded = customReport.internationalMaritimeOrganizationNumbers != null && customReport.internationalMaritimeOrganizationNumbers.trim().length > 0;
                    this.whereClauses = customReport.whereClause ? JSON.parse(customReport.whereClause) : [];
                    const columns = customReport.selectClause ? JSON.parse(customReport.selectClause) : [];
                    this.columns = customReport.customReportType && customReport.customReportType.uri? 
                        this.getEntityModelColumns(customReport.customReportType.uri, columns, true)
                        : columns;
                    this.item = customReport;
                    this.resetColumns();                
                    this.setValidator();
                    this.isLoading = false;
                })
            } else {
                this.item = {
                    entityType: EntityTypes.CustomReport,
                    identifier: Guid.generateGuid(),
                    customReportId: 0,
                    customReportTypeId: 0,
                    internationalMaritimeOrganizationNumbers: null,
                    name: null,
                    selectClause: null,
                    whereClause: null
                };
                this.setValidator();
                this.isLoading = false;
            }
        });
    }

    setValidator() {
        if (this.item) {
            const rulesHolder = {
                activation: ValidationActivation.Always,
                entityName: "CustomReport",
                isCollection: false,
                nestedRulesHolder: [],
                rules: [{
                    errorMessage: "Name is required",
                    parameters: null, property: "name",
                    validationType: ValidationTypeEnum.Required
                },
                {
                    errorMessage: "Report type is required",
                    parameters: JSON.stringify({ minimum: 1 }), property: "customReportTypeId",
                    validationType: ValidationTypeEnum.NumericRange
                }]
            };

            this.validator = this.entityValidationsService.getValidator(this.item, rulesHolder);
        }
    }

    filesInternalChanged() {
        if (this.filesInternal) {
            const file = this.filesInternal.item(0);
            if (file) {
                let reader = new FileReader();
                const imos = [];
                reader.onload = (e: any) => {

                    import('xlsx').then(XLSX => {
                        const data = e.target.result as string;
                        try {
                            const workbook = XLSX.read(data, { type: "binary" });
                            const sheet = workbook.Sheets[workbook.SheetNames[0]];
                            const range = XLSX.utils.decode_range(sheet["!ref"]);

                            for (let R = range.s.r; R <= range.e.r; ++R) {
                                for (let C = range.s.c; C <= range.e.c; ++C) {
                                    const cell_address = { c: C, r: R };
                                    /* if an A1-style address is needed, encode the address */
                                    const cell_ref = XLSX.utils.encode_cell(cell_address);

                                    if (sheet[cell_ref]) {

                                        const imo = sheet[cell_ref].v;

                                        if (imo && (typeof imo === 'number' || isFinite(Number(imo)))) {
                                            imos.push(imo);
                                        }
                                    }

                                }
                            }

                            this.item.internationalMaritimeOrganizationNumbers = imos.join(",");
                            this.isExcelUploaded = true;
                        }
                        catch{
                            this.notificationClientService.show("Error", "File cannot be opened due to incorrect format.", NotificationType.Error);
                        }

                        reader.onload = null;
                        reader = null;
                    });
                };

                reader.readAsBinaryString(file);
            }
        }
    }

    private getEntityModelColumns(uri: string, columns: DataGridColumn[], defaultHidden: boolean = false): DataGridColumn[]
    {
        const selectedModel = this.entityModels.filter(c => c.uri == uri)[0];
        if(!selectedModel) return;
        const propertiesToAdd = selectedModel.projectionProperties.filter(c => !columns.some(x => x.name == c.name));
        for (const item of propertiesToAdd) {
            const column: DataGridColumn = QueryColumnHelper.generateDefaultColumn(item, selectedModel);
            if(defaultHidden) column.isHidden = true;
            columns.push(column);
        }

        return columns;
    }

    private loadColumns() {
        if (!this.entityModels || !this.selectedCustomReportType || 
            !this.selectedCustomReportType.additionalData || !this.selectedCustomReportType.additionalData.uri) {
            return;
        }
        let columns = [];
        columns = this.getEntityModelColumns(this.selectedCustomReportType.additionalData.uri, columns);
        this.columns = columns;
        this.resetColumns();
    }

    setSelectedAvailableColumn(column: DataGridColumn) {
        this.selectedAvailableColumn = column;
    }

    moveColumnRight(column: DataGridColumn) {
        if (column) {
            column.isHidden = false;
        }
        this.resetColumns();
    }

    setSelectedVisibleColumn(column: DataGridColumn) {
        this.selectedVisibleColumn = column;
    }

    moveColumnLeft(column: DataGridColumn) {
        if (column) {
            column.isHidden = true;
        }
        this.resetColumns();
    }

    resetColumns() {
        const enumerable = Enumerable.from(this.columns);
        this.availableColumns = enumerable.where(x => typeof x.isHidden === "function" ? true : x.isHidden && this.containsQuery(x, this.availableColumnsQuery)).orderBy(x => x.columnOrder).toArray();
        this.visibleColumns = enumerable.where(x => !x.isHidden && this.containsQuery(x, this.visibleColumnsQuery)).orderBy(x => x.columnOrder).toArray();
    }

    public containsQuery(x: DataGridColumn, query: string) {
        return (!query || x.title.trim().toLowerCase().indexOf(query.trim().toLowerCase()) != -1);
    }

    public cancel() {
        this.dialogClientService.hide();
    }

    public itemChanged() {
        if (this.item) {
            setTimeout(() => {

                this._uow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.item, []);

                this._subscriptions.push(this.bindingEngine.propertyObserver(this.item, 'customReportTypeId').subscribe(() => {
                    this.loadColumns();
                }));

                if (this._whereQueryBuilder && this.whereClauses.length == 0 && !this.addFilterCalled) {
                    setTimeout(() => {
                        this._whereQueryBuilder.addFilter(0);
                        this.addFilterCalled = true;
                    })
                }
            });
        }
    }

    public selectedCustomReportTypeChanged() {
        this.loadColumns();
    }

    moveColumnUp() {
        if (this.selectedVisibleColumn) {
            this.fixColumnOrder();
            const list = Enumerable.from(this.visibleColumns);
            if (this.selectedVisibleColumn.columnOrder !== 0) {
                const prev = list.first(x => x.columnOrder === (this.selectedVisibleColumn.columnOrder - 1));
                prev.columnOrder += 1;
                this.selectedVisibleColumn.columnOrder -= 1;
            }
        }
        this.resetColumns();
    }

    moveColumnDown() {
        if (this.selectedVisibleColumn) {
            this.fixColumnOrder();
            const list = Enumerable.from(this.visibleColumns);
            if (this.selectedVisibleColumn.columnOrder !== (this.visibleColumns.length - 1)) {
                const next = list.first(x => x.columnOrder === (this.selectedVisibleColumn.columnOrder + 1));
                next.columnOrder -= 1;
                this.selectedVisibleColumn.columnOrder += 1;
            }
        }
        this.resetColumns();
    }

    fixColumnOrder() {
        for (const x of this.visibleColumns) {
            x.columnOrder = this.visibleColumns.indexOf(x);
        }
    }

    makeAllVisible() {
        this.fixColumnOrder();
        this.columns.forEach(c => c.isHidden = false);
        this.resetColumns();
    }

    makeAllAvailable() {
        this.fixColumnOrder();
        this.columns.forEach(c => c.isHidden = true);
        this.resetColumns();
    }

    public save() {
        return new Promise<void>((resolve, reject) => {

            this._uow.addChange('whereClause', JSON.stringify(this.whereClauses), []);
            this._uow.addChange('selectClause', JSON.stringify(this.columns), []);

            this.validator.validate().then(validationResponse => {

                if (validationResponse.valid) {
                    this.customReportService.saveCustomReport({
                        changes: this._uow.getChanges(),
                        id: this.item.customReportId
                    }).then(response => {
                        if (response.validationResult && response.validationResult.hasError) {
                            this.validationsClientService.showValidationErrors(response.validationResult);
                            reject();
                        } else {
                            if (this.onComplete) {
                                this.onComplete();
                            }
                            this.dialogClientService.hide();
                            resolve();
                        }
                    }).catch(response => {
                        if (response.validationResult && response.validationResult.hasError) {
                            this.validationsClientService.showValidationErrors(response.validationResult);
                        }
                        reject();
                    });
                } else {
                    resolve();
                }
            });
        });
    }

    public detached() {
        if (this._uow) {
            this._uow.dispose();
            this._uow = null;
        }

        this._subscriptions.forEach(c => c.dispose());
        this._subscriptions = [];
    }
}